import React, { useState, useEffect, useCallback } from "react";
import { api } from "./utils/api";
import "./Reservation.css";

const TYPE_OPTIONS = [
  { value: "PT",    label: "PT" },
  { value: "GROUP", label: "그룹" },
];

const STATUS_STYLE = {
  RESERVED:  { bg: "#D1ECE1", text: "#155724", border: "#A3D9B5", label: "예약중" },
  CANCELLED: { bg: "#F8D7DA", text: "#721C24", border: "#F5C6CB", label: "취소됨" },
};

function today() {
  return new Date().toISOString().slice(0, 10);
}
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}
function formatTime(t) {
  if (!t) return "-";
  return t.slice(0, 5);
}

const EMPTY_FORM = {
  member_id:  "",
  trainer_id: "",
  type:       "PT",
  title:      "",
  date:       today(),
  start_time: "09:00",
  end_time:   "10:00",
  memo:       "",
};

// 검색어 상태
const EMPTY_SEARCH = { member: "", trainer: "" };
export default function Reservation({ showToast, tenantId = 1, autoOpen, onAutoClose }) {
  const [viewMode, setViewMode] = useState("calendar");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const [calYear,  setCalYear]  = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(today());

  const [filterStatus, setFilterStatus] = useState("");
  const [filterType,   setFilterType]   = useState("");

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);

  const [detailItem, setDetailItem] = useState(null);
  const [search, setSearch] = useState(EMPTY_SEARCH);
  const [showMemberList, setShowMemberList] = useState(false);
  const [showTrainerList, setShowTrainerList] = useState(false);

  // ── 회원 & 트레이너 목록 로드
  useEffect(() => {
    api(`/members/?tenant_id=${tenantId}`)
      .then(data => setMembers(Array.isArray(data) ? data : data.items || []))
      .catch(() => {});
    api(`/staff/list?tenant_id=${tenantId}`)
      .then(data => setTrainers(Array.isArray(data) ? data : data.items || []))
      .catch(() => {});
  }, [tenantId]);

  // ── 데이터 로드
  const loadReservations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tenant_id: tenantId });
      if (filterStatus) params.set("status", filterStatus);
      if (filterType)   params.set("type", filterType);
      const data = await api(`/reservation/list?${params.toString()}`);
      setReservations(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast?.(e.message || "목록 불러오기 실패", "error");
    } finally {
      setLoading(false);
    }
  }, [tenantId, filterStatus, filterType]);

  useEffect(() => { loadReservations(); }, [loadReservations]);
  useEffect(() => {
  if (autoOpen) {
    openCreate();
  }
}, [autoOpen]);

  // ── 모달
  function openCreate(defaultDate) {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM, date: defaultDate || today() });
    setModalOpen(true);
  }
  function openEdit(item) {
    setEditTarget(item);
    setForm({
      member_id:  item.member_id  || "",
      trainer_id: item.trainer_id || "",
      type:       item.type       || "PT",
      title:      item.title      || "",
      date:       item.date       || today(),
      start_time: item.start_time?.slice(0, 5) || "09:00",
      end_time:   item.end_time?.slice(0, 5)   || "10:00",
      memo:       item.memo       || "",
    });
    setModalOpen(true);
    setDetailItem(null);
  }

  // ── 저장
  async function handleSave() {
    if (!form.title.trim()) { showToast?.("제목을 입력해주세요.", "error"); return; }
    if (!form.member_id)    { showToast?.("회원을 선택해주세요.", "error"); return; }
    setSaving(true);
    try {
      const body = {
        tenant_id:  tenantId,
        member_id:  Number(form.member_id),
        trainer_id: form.trainer_id ? Number(form.trainer_id) : null,
        type:       form.type,
        title:      form.title,
        date:       form.date,
        start_time: form.start_time + ":00",
        end_time:   form.end_time   + ":00",
        memo:       form.memo || null,
      };
      if (editTarget) {
        await api(`/reservation/${editTarget.id}?tenant_id=${tenantId}`, {
          method: "PUT", body: JSON.stringify(body),
        });
        showToast?.("예약이 수정되었습니다.", "success");
      } else {
        await api("/reservation", { method: "POST", body: JSON.stringify(body) });
        showToast?.("예약이 등록되었습니다.", "success");
      }
      setModalOpen(false);
      loadReservations();
    } catch (e) {
      showToast?.(e.message || "저장 실패", "error");
    } finally {
      setSaving(false);
    }
  }

  // ── 취소
  async function handleCancel(id) {
    if (!window.confirm("예약을 취소하시겠습니까?")) return;
    try {
      await api(`/reservation/${id}?tenant_id=${tenantId}`, { method: "DELETE" });
      showToast?.("예약이 취소되었습니다.", "success");
      setDetailItem(null);
      loadReservations();
    } catch (e) {
      showToast?.(e.message || "취소 실패", "error");
    }
  }

  // ── 이름 조회 헬퍼
  function getMemberName(id) {
    const m = members.find(m => m.id === id);
    return m ? m.name : `회원 #${id}`;
  }
  function getTrainerName(id) {
    const t = trainers.find(t => t.id === id);
    return t ? t.name : `트레이너 #${id}`;
  }

  // ── 캘린더 헬퍼
  const byDate = {};
  reservations.forEach((r) => {
    if (r.date) {
      if (!byDate[r.date]) byDate[r.date] = [];
      byDate[r.date].push(r);
    }
  });
  const selectedDayItems = byDate[selectedDate] || [];

  function prevMonth() {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  }
  function nextMonth() {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  }

  const monthNames = ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"];
  const dayNames   = ["일","월","화","수","목","금","토"];

  function renderCalendar() {
    const days     = getDaysInMonth(calYear, calMonth);
    const firstDay = getFirstDayOfMonth(calYear, calMonth);
    const cells    = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= days; d++)    cells.push(d);

    return (
      <div className="rsv-calendar">
        <div className="rsv-cal-header">
          <button className="rsv-cal-nav" onClick={prevMonth}>‹</button>
          <span className="rsv-cal-title">{calYear}년 {monthNames[calMonth]}</span>
          <button className="rsv-cal-nav" onClick={nextMonth}>›</button>
        </div>
        <div className="rsv-cal-grid">
          {dayNames.map((d, i) => (
            <div key={d} className={`rsv-cal-dayname ${i===0?"sun":i===6?"sat":""}`}>{d}</div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} className="rsv-cal-cell empty" />;
            const dateStr = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const items   = byDate[dateStr] || [];
            const active  = items.filter(r => r.status !== "CANCELLED");
            const isSelected = dateStr === selectedDate;
            const isToday    = dateStr === today();
            const dow = (firstDay + d - 1) % 7;
            return (
              <div
                key={d}
                className={`rsv-cal-cell ${isSelected?"selected":""} ${isToday?"today":""}`}
                onClick={() => setSelectedDate(dateStr)}
              >
                <span className={`rsv-cal-num ${dow===0?"sun":dow===6?"sat":""}`}>{d}</span>
                <div className="rsv-cal-dots">
                  {active.slice(0, 3).map((r) => {
                    const st = STATUS_STYLE[r.status];
                    return (
                      <span
                        key={r.id}
                        className="rsv-cal-dot-label"
                        style={{ background: st?.bg, color: st?.text }}
                        onClick={(e) => { e.stopPropagation(); setDetailItem(r); }}
                      >
                        [{r.type}] {r.title.length > 5 ? r.title.slice(0,5)+"…" : r.title}
                      </span>
                    );
                  })}
                  {active.length > 3 && <span className="rsv-cal-more">+{active.length - 3}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="rsv-day-panel">
          <div className="rsv-day-header">
            <span className="rsv-day-title">
              {selectedDate.slice(5).replace("-","월 ")}일 예약
              <span className="rsv-day-count">{selectedDayItems.length}건</span>
            </span>
            <button className="rsv-add-btn" onClick={() => openCreate(selectedDate)}>+ 예약 추가</button>
          </div>
          {selectedDayItems.length === 0
            ? <div className="rsv-empty">이 날의 예약이 없습니다.</div>
            : selectedDayItems.map((r) => renderCard(r))}
        </div>
      </div>
    );
  }

  function renderList() {
    return (
      <div className="rsv-list-wrap">
        <div className="rsv-list-filters">
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="rsv-filter-select">
            <option value="">전체 타입</option>
            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="rsv-filter-select">
            <option value="">전체 상태</option>
            <option value="RESERVED">예약중</option>
            <option value="CANCELLED">취소됨</option>
          </select>
          <button className="rsv-search-btn" onClick={loadReservations}>검색</button>
          <button className="rsv-add-btn" style={{marginLeft:"auto"}} onClick={() => openCreate()}>+ 예약 추가</button>
        </div>
        {loading
          ? <div className="rsv-loading">불러오는 중...</div>
          : reservations.length === 0
            ? <div className="rsv-empty">예약 내역이 없습니다.</div>
            : (
              <table className="rsv-table">
                <thead>
                  <tr>
                    <th>상태</th><th>타입</th><th>제목</th><th>회원</th>
                    <th>트레이너</th><th>날짜</th><th>시간</th><th>메모</th><th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => {
                    const st = STATUS_STYLE[r.status];
                    return (
                      <tr key={r.id} className="rsv-table-row" onClick={() => setDetailItem(r)}>
                        <td>
                          <span className="rsv-badge" style={{ background: st?.bg, color: st?.text, border:`1px solid ${st?.border}` }}>
                            {st?.label || r.status}
                          </span>
                        </td>
                        <td><span className="rsv-type-tag">{r.type}</span></td>
                        <td className="rsv-col-title">{r.title}</td>
                        <td>{getMemberName(r.member_id)}</td>
                        <td>{r.trainer_id ? getTrainerName(r.trainer_id) : "-"}</td>
                        <td>{r.date}</td>
                        <td>{formatTime(r.start_time)} ~ {formatTime(r.end_time)}</td>
                        <td className="rsv-col-memo">{r.memo || "-"}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {r.status !== "CANCELLED" && (
                            <div className="rsv-row-actions">
                              <button className="rsv-btn-edit" onClick={() => openEdit(r)}>수정</button>
                              <button className="rsv-btn-delete" onClick={() => handleCancel(r.id)}>취소</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
        }
      </div>
    );
  }

  function renderCard(r) {
    const st = STATUS_STYLE[r.status];
    return (
      <div
        key={r.id}
        className="rsv-card"
        style={{ borderLeft: `4px solid ${st?.text}`, opacity: r.status === "CANCELLED" ? 0.5 : 1 }}
        onClick={() => setDetailItem(r)}
      >
        <div className="rsv-card-top">
          <span className="rsv-card-title">
            <span className="rsv-type-tag">{r.type}</span> {r.title}
          </span>
          <span className="rsv-badge" style={{ background: st?.bg, color: st?.text }}>{st?.label}</span>
        </div>
        <div className="rsv-card-meta">
          <span>👤 {getMemberName(r.member_id)}</span>
          {r.trainer_id && <span>🏋️ {getTrainerName(r.trainer_id)}</span>}
          <span>⏰ {formatTime(r.start_time)} ~ {formatTime(r.end_time)}</span>
        </div>
      </div>
    );
  }

  function renderDetail() {
    if (!detailItem) return null;
    const r  = detailItem;
    const st = STATUS_STYLE[r.status];
    return (
      <div className="rsv-detail-overlay" onClick={() => setDetailItem(null)}>
        <div className="rsv-detail-panel" onClick={e => e.stopPropagation()}>
          <div className="rsv-detail-header">
            <span className="rsv-detail-title">{r.title}</span>
            <button className="rsv-detail-close" onClick={() => setDetailItem(null)}>✕</button>
          </div>
          <div className="rsv-detail-body">
            <div className="rsv-detail-row">
              <label>상태</label>
              <span className="rsv-badge" style={{ background: st?.bg, color: st?.text }}>{st?.label}</span>
            </div>
            <div className="rsv-detail-row"><label>타입</label><span>{r.type}</span></div>
            <div className="rsv-detail-row"><label>회원</label><span>{getMemberName(r.member_id)}</span></div>
            <div className="rsv-detail-row"><label>트레이너</label><span>{r.trainer_id ? getTrainerName(r.trainer_id) : "-"}</span></div>
            <div className="rsv-detail-row"><label>날짜</label><span>{r.date}</span></div>
            <div className="rsv-detail-row"><label>시간</label><span>{formatTime(r.start_time)} ~ {formatTime(r.end_time)}</span></div>
            {r.memo && <div className="rsv-detail-row"><label>메모</label><span>{r.memo}</span></div>}
          </div>
          {r.status !== "CANCELLED" && (
            <div className="rsv-detail-footer">
              <button className="rsv-btn-edit" onClick={() => openEdit(r)}>수정</button>
              <button className="rsv-btn-delete" onClick={() => handleCancel(r.id)}>예약 취소</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderModal() {
    if (!modalOpen) return null;
    return (
      <div className="rsv-modal-overlay" onClick={() => setModalOpen(false)}>
        <div className="rsv-modal" onClick={e => e.stopPropagation()}>
          <div className="rsv-modal-header">
            <span>{editTarget ? "예약 수정" : "예약 추가"}</span>
            <button onClick={() => { setModalOpen(false); onAutoClose?.(); }}>✕</button>
          </div>
          <div className="rsv-modal-body">
            <div className="rsv-form-group">
              <label>타입 <span className="rsv-required">*</span></label>
              <div className="rsv-status-btns">
                {TYPE_OPTIONS.map(t => (
                  <button
                    key={t.value} type="button"
                    className={`rsv-status-btn ${form.type === t.value ? "active" : ""}`}
                    style={form.type === t.value ? { background: "#5B5FEF", color: "#fff", borderColor: "#5B5FEF" } : {}}
                    onClick={() => setForm(f => ({ ...f, type: t.value }))}
                  >{t.label}</button>
                ))}
              </div>
            </div>
            <div className="rsv-form-group">
              <label>제목 <span className="rsv-required">*</span></label>
              <input type="text" placeholder="예) 오전 PT, 수영 레슨" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="rsv-input" />
            </div>
           <div className="rsv-form-row">
  <div className="rsv-form-group" style={{ position: "relative" }}>
    <label>회원 <span className="rsv-required">*</span></label>
    <input
      type="text"
      className="rsv-input"
      placeholder="이름으로 검색"
      value={search.member}
      onChange={e => {
        setSearch(s => ({ ...s, member: e.target.value }));
        setForm(f => ({ ...f, member_id: "" }));
        setShowMemberList(true);
      }}
      onFocus={() => setShowMemberList(true)}
      onBlur={() => setTimeout(() => setShowMemberList(false), 150)}
    />
    {form.member_id && (
      <div style={{ fontSize: 12, color: "#5B5FEF", marginTop: 4 }}>
        ✅ {getMemberName(Number(form.member_id))} 선택됨
      </div>
    )}
    {showMemberList && search.member && (
      <div style={{
        position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
        border: "1px solid #e0e0e0", borderRadius: 8, zIndex: 100,
        maxHeight: 180, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
      }}>
        {members
          .filter(m => m.name?.includes(search.member) || m.phone?.includes(search.member))
          .map(m => (
            <div
              key={m.id}
              style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f5f5f5" }}
              onMouseDown={() => {
                setForm(f => ({ ...f, member_id: String(m.id) }));
                setSearch(s => ({ ...s, member: m.name }));
                setShowMemberList(false);
              }}
            >
              {m.name} <span style={{ color: "#999", fontSize: 11 }}>{m.phone}</span>
            </div>
          ))
        }
        {members.filter(m => m.name?.includes(search.member) || m.phone?.includes(search.member)).length === 0 && (
          <div style={{ padding: "10px 14px", color: "#999", fontSize: 13 }}>검색 결과 없음</div>
        )}
      </div>
    )}
  </div>
  <div className="rsv-form-group" style={{ position: "relative" }}>
    <label>트레이너</label>
    <input
      type="text"
      className="rsv-input"
      placeholder="이름으로 검색 (선택사항)"
      value={search.trainer}
      onChange={e => {
        setSearch(s => ({ ...s, trainer: e.target.value }));
        setForm(f => ({ ...f, trainer_id: "" }));
        setShowTrainerList(true);
      }}
      onFocus={() => setShowTrainerList(true)}
      onBlur={() => setTimeout(() => setShowTrainerList(false), 150)}
    />
    {form.trainer_id && (
      <div style={{ fontSize: 12, color: "#5B5FEF", marginTop: 4 }}>
        ✅ {getTrainerName(Number(form.trainer_id))} 선택됨
      </div>
    )}
    {showTrainerList && search.trainer && (
      <div style={{
        position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
        border: "1px solid #e0e0e0", borderRadius: 8, zIndex: 100,
        maxHeight: 180, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
      }}>
        {trainers
          .filter(t => t.name?.includes(search.trainer))
          .map(t => (
            <div
              key={t.id}
              style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f5f5f5" }}
              onMouseDown={() => {
                setForm(f => ({ ...f, trainer_id: String(t.id) }));
                setSearch(s => ({ ...s, trainer: t.name }));
                setShowTrainerList(false);
              }}
            >
              {t.name}
            </div>
          ))
        }
        {trainers.filter(t => t.name?.includes(search.trainer)).length === 0 && (
          <div style={{ padding: "10px 14px", color: "#999", fontSize: 13 }}>검색 결과 없음</div>
        )}
      </div>
    )}
  </div>
</div>
            <div className="rsv-form-group">
              <label>날짜 <span className="rsv-required">*</span></label>
              <input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rsv-input" />
            </div>
            <div className="rsv-form-row">
              <div className="rsv-form-group">
                <label>시작 시간 <span className="rsv-required">*</span></label>
                <input type="time" value={form.start_time}
                  onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="rsv-input" />
              </div>
              <div className="rsv-form-group">
                <label>종료 시간</label>
                <input type="time" value={form.end_time}
                  onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="rsv-input" />
              </div>
            </div>
            <div className="rsv-form-group">
              <label>메모</label>
              <textarea placeholder="특이사항, 요청사항 등" value={form.memo}
                onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
                className="rsv-textarea" rows={3} />
            </div>
          </div>
          <div className="rsv-modal-footer">
            <button className="rsv-btn-cancel" onClick={() => { setModalOpen(false); onAutoClose?.(); }}>취소</button>
            <button className="rsv-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "저장 중..." : (editTarget ? "수정 완료" : "예약 등록")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rsv-container">
      <div className="rsv-page-header">
        <h2 className="rsv-page-title">예약 관리</h2>
        <div className="rsv-view-toggle">
          <button className={`rsv-toggle-btn ${viewMode==="calendar"?"active":""}`} onClick={() => setViewMode("calendar")}>📅 캘린더</button>
          <button className={`rsv-toggle-btn ${viewMode==="list"?"active":""}`} onClick={() => setViewMode("list")}>📋 리스트</button>
        </div>
      </div>
      {viewMode === "calendar" ? renderCalendar() : renderList()}
      {renderDetail()}
      {renderModal()}
    </div>
  );
}
